export const markdownToPlainText = (markdown: string): string => {
  if (!markdown) return "";

  let text = markdown;

  const decodeHtmlEntities = (input: string): string => {
    let output = input;

    // Numeric character references: &#123; and &#x1F600;
    output = output.replace(/&#(\d+);/g, (_m, dec: string) => {
      const codePoint = Number.parseInt(dec, 10);
      if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) return _m;
      return String.fromCodePoint(codePoint);
    });
    output = output.replace(/&#x([0-9a-fA-F]+);/g, (_m, hex: string) => {
      const codePoint = Number.parseInt(hex, 16);
      if (!Number.isFinite(codePoint) || codePoint < 0 || codePoint > 0x10ffff) return _m;
      return String.fromCodePoint(codePoint);
    });

    // A small set of common named entities we might see in chat content
    // (enough to avoid leaking HTML into plain text).
    output = output
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'");

    return output;
  };

  const stripInvisibleChars = (input: string): string =>
    input.replace(/(?:\u200B|\u200C|\u200D|\u2060|\uFEFF)/g, "");

  const safeDecodeURIComponent = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const displayUrl = (url: string): string => {
    const trimmed = url.trim();

    // For some schemes, the most useful "URL" to display is the identifier itself.
    // Ex: mailto:test@test.fr -> test@test.fr, tel:+331234 -> +331234
    const schemeMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
    const scheme = schemeMatch?.[1]?.toLowerCase();
    if (scheme === "mailto" || scheme === "tel" || scheme === "sms") {
      const withoutScheme = trimmed.slice(scheme.length + 1);
      // mailto: uses ?, tel:/sms: often use ? or ; parameters (RFC 3966)
      const valuePart = withoutScheme.split(/[?;]/)[0] ?? "";
      return safeDecodeURIComponent(valuePart).trim();
    }

    return trimmed;
  };

  // Preserve code content but drop fenced markers and optional language identifiers
  text = text.replace(/```(?:[a-zA-Z0-9]+\n)?([\s\S]*?)```/g, (_match, code) => code.trim());

  // Inline code: `code` -> code
  text = text.replace(/`([^`]+)`/g, "$1");

  // Images: ![alt](url) -> alt (url)
  text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_match, alt, url) =>
    [alt, url].filter(Boolean).join(" ")
  );

  // Links:
  // - links: [label](url) -> label (url)
  // - links: [url](url) -> url
  // - mailto: [label](mailto:url) -> label (url)
  // - mailto: [url](mailto:url) -> url
  // - tel: [label](tel:url) -> label (url)
  // - tel: [url](tel:url) -> url

  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label: string, url: string) => {
    const trimmedLabel = label.trim();
    const shownUrl = displayUrl(url);

    // Same logic for every link: if label already equals the (displayed) URL, show it once.
    if (trimmedLabel === shownUrl) return trimmedLabel;
    return `${trimmedLabel} (${shownUrl})`;
  });

  // Headings: # Title -> Title
  text = text.replace(/^#{1,6}\s+/gm, "");

  // Blockquotes: > text -> text
  text = text.replace(/^>\s?/gm, "");

  // Horizontal rules: --- or *** or ___ -> remove entire line
  text = text.replace(/^\s*[-*_]{3,}\s*$/gm, "");

  // List markers: -, *, +, 1. -> (remove marker, keep content)
  text = text.replace(/^\s*([-*+]|\d+\.)\s+/gm, "");

  // Bold / strong: **text** or __text__ -> text
  text = text.replace(/(\*\*|__)(.*?)\1/g, "$2");

  // Emphasis: *text* or _text_ -> text
  text = text.replace(/(\*|_)(.*?)\1/g, "$2");

  // Remove any residual lines made only of *, - or _
  text = text.replace(/^\s*[-*_]+\s*$/gm, "");

  // Collapse 2+ newlines into a single newline
  text = text.replace(/\n{2,}/g, "\n");

  // Decode HTML entities that may appear in text, then remove invisible chars (e.g. ZWSP).
  text = stripInvisibleChars(decodeHtmlEntities(text));

  return text.trim();
};
